import React from 'react';
import Footer from '../components/common/Footer';
import SiteHeader from '../components/common/SiteHeader';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  UserGroupIcon,
  ServerIcon,
  GlobeAltIcon,
  ClockIcon,
  EnvelopeIcon,
  CogIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const CookiePolicy: React.FC = () => {
  const tableOfContents = [
    { id: '1-introduction', title: 'Introduction', icon: DocumentTextIcon },
    { id: '2-what-are-cookies', title: 'What Are Cookies', icon: CogIcon },
    { id: '3-types-of-cookies-we-use', title: 'Types of Cookies We Use', icon: ServerIcon },
    { id: '4-how-we-use-cookies', title: 'How We Use Cookies', icon: EyeIcon },
    { id: '5-third-party-cookies', title: 'Third-Party Cookies', icon: GlobeAltIcon },
    { id: '6-cookie-management', title: 'Cookie Management', icon: ShieldCheckIcon },
    { id: '7-data-retention', title: 'Data Retention', icon: ClockIcon },
    { id: '8-updates-to-policy', title: 'Updates to This Policy', icon: DocumentTextIcon },
    { id: '9-contact-us', title: 'Contact Us', icon: EnvelopeIcon }
  ];

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <SiteHeader />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-16">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Cookie Policy
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              TWENTY FOUR CAPITAL LLC
            </motion.p>
            <motion.div 
              className="mt-4 text-white/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>Effective Date: {currentDate}</p>
              <p>Last Updated: {currentDate}</p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-32 bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                          window.history.pushState(null, '', `#${item.id}`);
                        }
                      }}
                    >
                      <item.icon className="h-4 w-4 text-gray-400" />
                      <span>{item.title}</span>
                    </motion.a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-12">
                {/* Section 1: Introduction */}
                <section id="1-introduction">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">1.</span> Introduction
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      This Cookie Policy explains how Twenty Four Capital LLC ("Company," "we," "us," or "our") uses cookies and similar tracking technologies on our website and services. It describes what cookies are, how we use them, and your choices regarding their use.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      By using our website and services, you consent to the use of cookies in accordance with this policy. If you do not agree to our use of cookies, you should set your browser settings accordingly or refrain from using our website.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      This policy should be read alongside our Privacy Policy, which explains how we collect, use, and protect your personal information.
                    </p>
                  </div>
                </section>

                {/* Section 2: What Are Cookies */}
                <section id="2-what-are-cookies">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">2.</span> What Are Cookies
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Cookies can be categorized in several ways:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                      <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser</li>
                      <li><strong>Persistent Cookies:</strong> Cookies that remain on your device for a set period</li>
                      <li><strong>First-Party Cookies:</strong> Cookies set by our website</li>
                      <li><strong>Third-Party Cookies:</strong> Cookies set by external services we use</li>
                    </ul>
                  </div>
                </section>

                {/* Section 3: Types of Cookies We Use */}
                <section id="3-types-of-cookies-we-use">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">3.</span> Types of Cookies We Use
                  </h2>

                  <div className="space-y-8">
                    {/* 3.1 Essential Cookies */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Essential Cookies</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        These cookies are necessary for the website to function properly and cannot be disabled. They include:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Authentication cookies for secure login</li>
                        <li>Session management cookies</li>
                        <li>Security and fraud prevention cookies</li>
                        <li>Load balancing cookies</li>
                        <li>Cookie consent management</li>
                      </ul>
                    </div>

                    {/* 3.2 Functional Cookies */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Functional Cookies</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        These cookies enable enhanced functionality and personalization:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Language preference cookies</li>
                        <li>User interface customization</li>
                        <li>Form auto-completion</li>
                        <li>User preference storage</li>
                        <li>Feature usage tracking</li>
                      </ul>
                    </div>

                    {/* 3.3 Analytics Cookies */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Analytics Cookies</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        These cookies help us understand how visitors interact with our website:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Page view tracking</li>
                        <li>User journey analysis</li>
                        <li>Performance monitoring</li>
                        <li>Error tracking</li>
                        <li>Conversion rate analysis</li>
                      </ul>
                    </div>

                    {/* 3.4 Marketing Cookies */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.4 Marketing Cookies</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        These cookies are used to deliver relevant advertisements and track marketing campaign performance:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Ad targeting and personalization</li>
                        <li>Campaign effectiveness measurement</li>
                        <li>Retargeting cookies</li>
                        <li>Social media integration</li>
                        <li>Email marketing tracking</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 4: How We Use Cookies */}
                <section id="4-how-we-use-cookies">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">4.</span> How We Use Cookies
                  </h2>

                  <div className="space-y-6">
                    {[
                      {
                        title: "4.1 Website Functionality",
                        items: [
                          "Enable secure user authentication and login",
                          "Maintain user sessions and preferences",
                          "Remember your language and regional settings",
                          "Provide personalized content and features",
                          "Ensure website security and prevent fraud"
                        ]
                      },
                      {
                        title: "4.2 Performance and Analytics",
                        items: [
                          "Monitor website performance and loading times",
                          "Analyze user behavior and navigation patterns",
                          "Identify and fix technical issues",
                          "Optimize website design and functionality",
                          "Track conversion rates and user engagement"
                        ]
                      },
                      {
                        title: "4.3 User Experience",
                        items: [
                          "Remember your preferences and settings",
                          "Provide relevant content and recommendations",
                          "Improve website navigation and usability",
                          "Enable social media sharing and integration",
                          "Support customer service and support features"
                        ]
                      },
                      {
                        title: "4.4 Marketing and Advertising",
                        items: [
                          "Deliver targeted advertisements",
                          "Measure marketing campaign effectiveness",
                          "Enable retargeting campaigns",
                          "Track email marketing engagement",
                          "Analyze marketing ROI and performance"
                        ]
                      }
                    ].map((section, index) => (
                      <div key={index}>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">{section.title}</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          {section.items.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 5: Third-Party Cookies */}
                <section id="5-third-party-cookies">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">5.</span> Third-Party Cookies
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We use various third-party services that may set cookies on your device:
                  </p>
                  
                  <div className="space-y-6">
                    {[
                      {
                        service: "Google Analytics",
                        purpose: "Website analytics and performance monitoring",
                        cookies: ["_ga", "_gid", "_gat", "_gac"]
                      },
                      {
                        service: "Google Ads",
                        purpose: "Advertising and retargeting campaigns",
                        cookies: ["_gcl_au", "1P_JAR", "AID", "TAID"]
                      },
                      {
                        service: "Facebook Pixel",
                        purpose: "Social media advertising and analytics",
                        cookies: ["_fbp", "_fbc", "fr"]
                      },
                      {
                        service: "LinkedIn Insight",
                        purpose: "Professional network advertising",
                        cookies: ["li_gc", "li_mc", "UserMatchHistory"]
                      },
                      {
                        service: "Hotjar",
                        purpose: "User behavior analysis and heatmaps",
                        cookies: ["_hjid", "_hjSessionUser_*", "_hjFirstSeen"]
                      }
                    ].map((service, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-800 mb-2">{service.service}</h3>
                        <p className="text-gray-700 mb-2">{service.purpose}</p>
                        <p className="text-sm text-gray-600">
                          <strong>Cookies:</strong> {service.cookies.join(", ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 6: Cookie Management */}
                <section id="6-cookie-management">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">6.</span> Cookie Management
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Browser Settings</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        You can control and manage cookies through your browser settings:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Chrome: Settings → Privacy and security → Cookies and other site data</li>
                        <li>Firefox: Options → Privacy & Security → Cookies and Site Data</li>
                        <li>Safari: Preferences → Privacy → Manage Website Data</li>
                        <li>Edge: Settings → Cookies and site permissions → Cookies</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Cookie Consent</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        When you first visit our website, you'll see a cookie consent banner that allows you to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Accept all cookies</li>
                        <li>Reject non-essential cookies</li>
                        <li>Customize your cookie preferences</li>
                        <li>Learn more about our cookie usage</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Opt-Out Options</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        You can opt out of certain types of cookies:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Analytics cookies through browser settings</li>
                        <li>Marketing cookies through our consent manager</li>
                        <li>Third-party advertising through industry opt-out tools</li>
                        <li>Email tracking through unsubscribe links</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.4 Impact of Disabling Cookies</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Please note that disabling certain cookies may affect website functionality, user experience, and the availability of some features. Essential cookies cannot be disabled as they are necessary for basic website operation.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 7: Data Retention */}
                <section id="7-data-retention">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">7.</span> Data Retention
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Cookie retention periods vary based on their type and purpose:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Session cookies: Until browser is closed</li>
                    <li>Authentication cookies: Up to 30 days</li>
                    <li>Analytics cookies: Up to 2 years</li>
                    <li>Marketing cookies: Up to 1 year</li>
                    <li>Preference cookies: Up to 1 year</li>
                  </ul>
                </section>

                {/* Section 8: Updates to Policy */}
                <section id="8-updates-to-policy">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">8.</span> Updates to This Policy
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update this Cookie Policy periodically to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website with a new effective date.
                  </p>
                </section>

                {/* Section 9: Contact Us */}
                <section id="9-contact-us">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">9.</span> Contact Us
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have questions about this Cookie Policy or our use of cookies, contact us:
                  </p>
                  
                  <div className="bg-orange-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Twenty Four Capital LLC</h3>
                    <p className="text-gray-700 mb-1">1309 Coffeen Ave Ste 17816</p>
                    <p className="text-gray-700 mb-3">Sheridan, WY 82801</p>
                    <p className="text-gray-700 mb-1">Email: privacy@24lp.io</p>
                    <p className="text-gray-700">Website: https://24lp.io</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed">
                    For cookie-related complaints or concerns, you may also contact your local data protection authority.
                  </p>
                </section>
              </div>
            </main>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default CookiePolicy; 